import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EventBusService, EventPayload } from 'src/app/shared/services/event-bus.service';
import { LocumsMarginCalculatorEvents } from '../enums/locums-margin-calculator-events.enum';
import { AdditionalRatesStore } from './additional-rates.store';
import { AdditionalRate } from '../../models/additional-rate.model';
import { AdditionalRateTypes } from '../../enums/additional-rate-types.enum';
import { MarginSetupStore } from 'src/app/recruiting/locums-margin-calculator/store/margin-setup/margin-setup.store';
import { PayPeriodTypeEnum } from 'src/app/shared/models/enums/pay-period-type.enum';

@Injectable({ providedIn: 'root' })
export class AdditionalRatesReducer {
    private readonly eventBus = inject(EventBusService);
    private readonly additionalRatesStore = inject(AdditionalRatesStore);
    private readonly marginSetupStore = inject(MarginSetupStore);

    constructor() {
        this.listenToJobDetailsLoadedEvent();
        this.listenToAdditionalRatesEvent();
        this.listenToAdditionalRateFieldsEvent();
        this.listenToLoadMarginDetailsEvent();
    }

    private listenToJobDetailsLoadedEvent() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.JOB_DETAILS_LOADED)
            .pipe(takeUntilDestroyed())
            .subscribe((event: EventPayload) => {
                const validPayPeriodTypes = [PayPeriodTypeEnum.Hourly, PayPeriodTypeEnum.Daily].map((x) => x as number);
                if (
                    !event.payload?.twentyFourHourCallBillRate ||
                    this.marginSetupStore.marginId() ||
                    !validPayPeriodTypes.includes(event.payload?.payPeriodTypeId)
                ) {
                    return;
                }

                const twentyFourHourCallAdditionalRate: AdditionalRate = {
                    marginAdditionalRateId: null,
                    marginId: null,
                    additionalRateTypeId: AdditionalRateTypes.ON_CALL_24HOURS,
                    marginCoverageTypeId: null,
                    billRate: event.payload.twentyFourHourCallBillRate as number,
                    payRate: null,
                    expectedShifts: null,
                    expectedHours: null,
                    isDeleted: false
                };

                this.additionalRatesStore.addAdditionalRate(twentyFourHourCallAdditionalRate);

                this.eventBus.emit({
                    type: LocumsMarginCalculatorEvents.ADDITIONAL_RATE_24_HOURS_PRE_POPULATE_BILL_RATE_LOADED,
                    payload: twentyFourHourCallAdditionalRate
                });
            });
    }

    private listenToLoadMarginDetailsEvent() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_CALCULATOR_LOAD_MARGIN_DETAILS)
            .pipe(takeUntilDestroyed())
            .subscribe((event: EventPayload) => {
                this.additionalRatesStore.updateAdditionalRateFromData(
                    event.payload.marginDetails.marginAdditionalRates
                );
            });
    }

    private listenToAdditionalRatesEvent() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_ADDED)
            .pipe(takeUntilDestroyed())
            .subscribe((event: EventPayload) => {
                const additionalRate: AdditionalRate = {
                    marginAdditionalRateId: null,
                    marginId: event.payload.marginId as number,
                    additionalRateTypeId: event.payload.additionalRateTypeId as number,
                    marginCoverageTypeId: null,
                    billRate: null,
                    payRate: null,
                    expectedShifts: null,
                    expectedHours: null,
                    isDeleted: false
                };

                const additionalRates = event.payload.additionalRates as AdditionalRate[];

                const existingRate = additionalRates?.find(
                    (rate) => rate.additionalRateTypeId === additionalRate.additionalRateTypeId
                );
                if (existingRate) {
                    additionalRate.marginAdditionalRateId = existingRate.marginAdditionalRateId;
                    this.additionalRatesStore.updateAdditionalRateField(additionalRate);
                } else {
                    this.additionalRatesStore.addAdditionalRate(additionalRate);
                }
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_REMOVED)
            .pipe(takeUntilDestroyed())
            .subscribe((event: EventPayload) => {
                const additionalRateTypeId = event.payload.additionalRateTypeId as AdditionalRateTypes;
                const additionalRates = event.payload.additionalRates as AdditionalRate[];

                if (
                    additionalRates?.some(
                        (rate) => rate.marginAdditionalRateId && rate.additionalRateTypeId === additionalRateTypeId
                    )
                ) {
                    this.additionalRatesStore.updateAdditionalRateField({
                        additionalRateTypeId: additionalRateTypeId,
                        isDeleted: true
                    });
                } else {
                    this.additionalRatesStore.removeAdditionalRate(additionalRateTypeId);
                }
            });
    }

    private listenToAdditionalRateFieldsEvent() {
        this.listenToAdditionalRateOnCall24HoursFieldsEvent();
        this.listenToAdditionalRateWeekendCallFieldsEvent();
        this.listenToAdditionalRateNightShiftFieldsEvent();
        this.listenToAdditionalRateCallBackFieldsEvent();
    }

    private listenToAdditionalRateCallBackFieldsEvent() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_CALL_BACK_BILL_RATE_CHANGED)
            .pipe(takeUntilDestroyed())
            .subscribe((event: EventPayload) => {
                this.additionalRatesStore.updateAdditionalRateField({
                    additionalRateTypeId: AdditionalRateTypes.CALL_BACK,
                    billRate: event.payload.billRate as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_CALL_BACK_PAY_RATE_CHANGED)
            .pipe(takeUntilDestroyed())
            .subscribe((event: EventPayload) => {
                this.additionalRatesStore.updateAdditionalRateField({
                    additionalRateTypeId: AdditionalRateTypes.CALL_BACK,
                    payRate: event.payload.payRate as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_CALL_BACK_EXPD_HOURS_CHANGED)
            .pipe(takeUntilDestroyed())
            .subscribe((event: EventPayload) => {
                this.additionalRatesStore.updateAdditionalRateField({
                    additionalRateTypeId: AdditionalRateTypes.CALL_BACK,
                    expectedHours: event.payload.expectedHours as number
                });
            });
    }

    private listenToAdditionalRateNightShiftFieldsEvent() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_NIGHT_SHIFT_CALL_MARGIN_COVERAGE_TYPE_CHANGED)
            .pipe(takeUntilDestroyed())
            .subscribe((event: EventPayload) => {
                this.additionalRatesStore.updateAdditionalRateField({
                    additionalRateTypeId: AdditionalRateTypes.NIGHT_SHIFT_CALL,
                    marginCoverageTypeId: event.payload.marginCoverageTypeId as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_NIGHT_SHIFT_CALL_BILL_RATE_CHANGED)
            .pipe(takeUntilDestroyed())
            .subscribe((event: EventPayload) => {
                this.additionalRatesStore.updateAdditionalRateField({
                    additionalRateTypeId: AdditionalRateTypes.NIGHT_SHIFT_CALL,
                    billRate: event.payload.billRate as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_NIGHT_SHIFT_CALL_PAY_RATE_CHANGED)
            .pipe(takeUntilDestroyed())
            .subscribe((event: EventPayload) => {
                this.additionalRatesStore.updateAdditionalRateField({
                    additionalRateTypeId: AdditionalRateTypes.NIGHT_SHIFT_CALL,
                    payRate: event.payload.payRate as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_NIGHT_SHIFT_CALL_EXPD_SHIFTS_CHANGED)
            .pipe(takeUntilDestroyed())
            .subscribe((event: EventPayload) => {
                this.additionalRatesStore.updateAdditionalRateField({
                    additionalRateTypeId: AdditionalRateTypes.NIGHT_SHIFT_CALL,
                    expectedShifts: event.payload.expectedShifts as number
                });
            });
    }

    private listenToAdditionalRateWeekendCallFieldsEvent() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_WEEKEND_CALL_MARGIN_COVERAGE_TYPE_CHANGED)
            .pipe(takeUntilDestroyed())
            .subscribe((event: EventPayload) => {
                this.additionalRatesStore.updateAdditionalRateField({
                    additionalRateTypeId: AdditionalRateTypes.WEEKEND_CALL,
                    marginCoverageTypeId: event.payload.marginCoverageTypeId as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_WEEKEND_CALL_BILL_RATE_CHANGED)
            .pipe(takeUntilDestroyed())
            .subscribe((event: EventPayload) => {
                this.additionalRatesStore.updateAdditionalRateField({
                    additionalRateTypeId: AdditionalRateTypes.WEEKEND_CALL,
                    billRate: event.payload.billRate as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_WEEKEND_CALL_PAY_RATE_CHANGED)
            .pipe(takeUntilDestroyed())
            .subscribe((event: EventPayload) => {
                this.additionalRatesStore.updateAdditionalRateField({
                    additionalRateTypeId: AdditionalRateTypes.WEEKEND_CALL,
                    payRate: event.payload.payRate as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_WEEKEND_CALL_EXPD_SHIFTS_CHANGED)
            .pipe(takeUntilDestroyed())
            .subscribe((event: EventPayload) => {
                this.additionalRatesStore.updateAdditionalRateField({
                    additionalRateTypeId: AdditionalRateTypes.WEEKEND_CALL,
                    expectedShifts: event.payload.expectedShifts as number
                });
            });
    }

    private listenToAdditionalRateOnCall24HoursFieldsEvent() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_ON_CALL_24HOURS_MARGIN_COVERAGE_TYPE_CHANGED)
            .pipe(takeUntilDestroyed())
            .subscribe((event: EventPayload) => {
                this.additionalRatesStore.updateAdditionalRateField({
                    additionalRateTypeId: AdditionalRateTypes.ON_CALL_24HOURS,
                    marginCoverageTypeId: event.payload.marginCoverageTypeId as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_ON_CALL_24HOURS_BILL_RATE_CHANGED)
            .pipe(takeUntilDestroyed())
            .subscribe((event: EventPayload) => {
                this.additionalRatesStore.updateAdditionalRateField({
                    additionalRateTypeId: AdditionalRateTypes.ON_CALL_24HOURS,
                    billRate: event.payload.billRate as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_ON_CALL_24HOURS_PAY_RATE_CHANGED)
            .pipe(takeUntilDestroyed())
            .subscribe((event: EventPayload) => {
                this.additionalRatesStore.updateAdditionalRateField({
                    additionalRateTypeId: AdditionalRateTypes.ON_CALL_24HOURS,
                    payRate: event.payload.payRate as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_ON_CALL_24HOURS_EXPD_SHIFTS_CHANGED)
            .pipe(takeUntilDestroyed())
            .subscribe((event: EventPayload) => {
                this.additionalRatesStore.updateAdditionalRateField({
                    additionalRateTypeId: AdditionalRateTypes.ON_CALL_24HOURS,
                    expectedShifts: event.payload.expectedShifts as number
                });
            });
    }
}
